import { Box, Typography, Grid } from "@mui/material"

const DetailField = ({ label, value, color = "#666" }) => (
    <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color, fontWeight: 500, mb: 0.5 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 400 }}>
            {value || "N/A"}
        </Typography>
    </Box>
)

export default function SingleOrderDetails({
    values,
    stopData,
    stopIndex,
    stops,
    getShipperValue,
    getConsigneeValue,
    getBillingPartyValue,
    getStopValue,
}) {
    // Format date helper
    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A"
        try {
            return new Date(dateString).toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            })
        } catch {
            return dateString
        }
    }

    return (
        <Box sx={{ p: 3, backgroundColor: "white" }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: "text.primary" }}>
                Details
            </Typography>

            <Grid container spacing={4}>
                {/* SHIPPER Section */}
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ color: "#1976d2", fontWeight: 600, mb: 2 }}>
                        SHIPPER
                    </Typography>

                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {stopData?.shipper?.name || stopData?.shipperAddress?.company || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {stopData?.shipperAddress?.address?.address || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                        {`${stopData?.shipperAddress?.address?.city || "N/A"}, ${stopData?.shipperAddress?.address?.state || "N/A"}, ${stopData?.shipperAddress?.address?.country || "N/A"} ${stopData?.shipperAddress?.address?.postal || "N/A"}`}
                    </Typography>

                    <DetailField
                        label={stops?.[stopIndex]?.isAppointmentPickup ? "Pickup Time Start:" : "Ready Time Start:"}
                        value={formatDateTime(
                            stops?.[stopIndex]?.isAppointmentPickup
                                ? stops?.[stopIndex]?.pickUpTimeStart || stops?.[stopIndex]?.pickUpTimeStart
                                : stops?.[stopIndex]?.readyTimeStart || stops?.[stopIndex]?.readyTimeStart,
                        )}
                    />

                    <DetailField
                        label={stops?.[stopIndex]?.isAppointmentPickup ? "Pickup Time End:" : "Delivery Time End:"}
                        value={formatDateTime(
                            stops?.[stopIndex]?.isAppointmentPickup
                                ? stops?.[stopIndex]?.pickUpTimeEnd || stops?.[stopIndex]?.pickUpTimeEnd
                                : stops?.[stopIndex]?.readyTimeEnd || stops?.[stopIndex]?.readyTimeEnd,
                        )}
                    />

                    <DetailField label="Customs Broker:" value={getStopValue("customsBroker")} />

                    <DetailField label="Port of Entry:" value={getStopValue("portOfEntry")} />

                    <DetailField
                        label="Declared Value:"
                        value={getStopValue("declaredValue") ? `$ ${getStopValue("declaredValue")} CAD` : "$ 0.00 CAD"}
                    />

                    <DetailField label="Special Instructions:" value={getStopValue("instructions")} />

                    <DetailField 
                        label="Accessorials:" 
                        value={Array.isArray(getStopValue("accessorials")) 
                            ? getStopValue("accessorials").map(accessorial => accessorial?.name).filter(Boolean).join(", ") 
                            : "None"} 
                    />
                </Grid>

                {/* CONSIGNEE Section */}
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ color: "#1976d2", fontWeight: 600, mb: 2 }}>
                        CONSIGNEE
                    </Typography>

                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {getConsigneeValue("name") || stopData?.consigneeAddress?.company || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {getConsigneeValue("address.address") || stopData?.consigneeAddress?.address?.address || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                        {`${getConsigneeValue("address.city") || stopData?.consigneeAddress?.address?.city || "N/A"}, ${getConsigneeValue("address.state") || stopData?.consigneeAddress?.address?.state || "N/A"}, ${getConsigneeValue("address.country") || stopData?.consigneeAddress?.address?.country || "N/A"} ${getConsigneeValue("address.postal") || stopData?.consigneeAddress?.address?.postal || "N/A"}`}
                    </Typography>

                    <DetailField
                        label={stops?.[stopIndex]?.isAppointmentDelivery ? "Requested Delivery Start: " : "Requested Delivery: "}
                        value={
                            formatDateTime(
                                stops?.[stopIndex]?.isAppointmentDelivery
                                    ? stops?.[stopIndex]?.deliveryTimeStart || stops?.[stopIndex]?.deliveryTimeStart
                                    : stops?.[stopIndex]?.requestedTimeStart || stops?.[stopIndex]?.requestedTimeStart,
                            ) || "No Date Added"
                        }
                    />

                    <DetailField
                        label={stops?.[stopIndex]?.isAppointmentDelivery ? "Requested Delivery End: " : "Requested Delivery End: "}
                        value={formatDateTime(
                            stops?.[stopIndex]?.isAppointmentDelivery
                                ? stops?.[stopIndex]?.deliveryTimeEnd || stops?.[stopIndex]?.deliveryTimeEnd
                                : stops?.[stopIndex]?.requestedTimeEnd || stops?.[stopIndex]?.requestedTimeEnd,
                        )}
                    />

                    <DetailField label="PO Number:" value={getStopValue("poNumber")} />

                    <DetailField label="REF Number:" value={getStopValue("refNumber")} />

                    <DetailField label="Load Tender Number:" value={getStopValue("tenderNumber")} />
                </Grid>

                {/* BILLING PARTY Section */}
                <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" sx={{ color: "#1976d2", fontWeight: 600, mb: 2 }}>
                        BILLING PARTY
                    </Typography>

                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {getBillingPartyValue("name") || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {getBillingPartyValue("addresses.address") || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                        {`${getBillingPartyValue("addresses.city") || "N/A"}, ${getBillingPartyValue("addresses.state") || "N/A"}, ${getBillingPartyValue("addresses.country") || "N/A"} ${getBillingPartyValue("addresses.postal") || "N/A"}`}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    )
}
